import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { CHAT_ORCHESTRATOR_FLAG } from "../../../shared/featureFlagNames";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  workflowPageViewSchema,
  type WorkflowPageView,
} from "../../../shared/workflowPageView";
import { useChatpadShortcut } from "../../hooks/useChatpadShortcut";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useSessionTenantEdgeUrlOptions } from "../../hooks/useSessionTenantOrigin";
import { useToolOutput } from "../../hooks/useToolOutput";
import { clientSendContext } from "../../lib/clientSendContext";
import { emitErrorToast } from "../../lib/errorToast";
import { personalVolumePath } from "../../lib/apiPaths";
import { resolveStorageFileHref } from "../../lib/resolveStorageFileHref";
import { getPageUrl } from "../../lib/stepUrl";
import { uploadToolCallOutput } from "../../lib/toolCallOutputs";
import { showWorkflowPage } from "../../tools/ShowWorkflowPage";
import {
  approvePlan,
  askQuestion,
  bash,
  type ChatOutputView,
  type ChatTool,
  connectChatToApp,
  connectToApp,
  editFile,
  execute,
  readFile,
  readSkillFile,
  readStorageFile,
  runSkillScript,
  setWorkflowSuggestion,
  showExecutionOutput,
  showStorageFile,
  skillTool,
  webFetch,
  webSearch,
  writeFile,
} from "../../tools";
import { Chat } from "../Chat";
import { ChatMessageNav } from "../Chat/ChatMessageNav";
import type { ChatHandle, ChatProps, ElementSnippet } from "../Chat/chatTypes";
import { FilePreviewPanel } from "../AttachmentHoverPreview";
import type { FilePreviewTarget } from "../FileRender";
import { ImportWorkflowModal } from "../ImportWorkflowModal";
import { ChatWorkspaceLayout } from "./ChatWorkspaceLayout";
import { ChatpadToggleButton } from "./ChatpadToggleButton";
import {
  ResearchSourcesPanelContext,
  type ResearchSourceGroup,
} from "../Chat/ResearchCitations";
import { SourcesPanel } from "../Chat/ResearchSourceViews";
import { readOnlyStyle } from "./readOnlyStyle";
import { ChatActionsMenu } from "./ChatActionsMenu";
import { deriveChatOutputs } from "./chatOutputs";
import { DeriveWorkflowButton } from "./DeriveWorkflowButton";
import { Chatpad, type ChatpadSection } from "./Chatpad";
import { OutputsList } from "./OutputsList";
import { PersonalChatConnectorsBar } from "./PersonalChatConnectorsBar";
import { PersonalChatVolumesButton } from "./PersonalChatVolumesButton";
import { subAgentTools } from "./SubAgents";
import { SubAgentPage } from "./SubAgentPage";
import { SubAgentPrompt, usePendingSubAgentCall } from "./SubAgentPrompt";
import { chatSuggestions } from "./chatSlashSuggestions";
import { greetingName, NewChatEmptyState } from "./NewChatEmptyState";
import { getInvestigationContent } from "../../../shared/chatInvestigation";
import { useChatInvestigation } from "./useChatInvestigation";
import { ChatInvestigationSource } from "./ChatInvestigationSource";
import { pickChatIconColor } from "./pickChatIconColor";
import {
  type WorkflowPageLink,
  WorkflowPagePreview,
  workflowPageLink,
} from "./WorkflowPagePreview";
import { mentionConnectorCatalog } from "../../hooks/useMentions";
import { PanelTransition } from "../PanelTransition";
import { StepDetails } from "../StepDetails";

type ChatRow = {
  id: string;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null;
  awaitingAction: boolean | null;
  entries: readonly NonNullable<
    Parameters<typeof Chat>[0]["entries"]
  >[number][];
};

type Props = {
  personalChatId: string;
  chatOwnerId: string;
  chat: ChatRow;
  pending?: boolean;
  readOnly?: boolean;
  onRenameChat?: (() => void) | undefined;
  onForkChat?: (() => void) | undefined;
  onDownloadChat?: (() => void) | undefined;
  onCloseChat?: (() => void) | undefined;
  onTogglePin?: (() => void) | undefined;
  pinned?: boolean;
};

const Container = styled("div")`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  container-type: inline-size;
`;

const ScrollArea = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  container-type: inline-size;
  margin-bottom: 32px;
  transition: margin-bottom 160ms ease-out;
  @container (max-width: 768px) {
    margin-bottom: 8px;
  }
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChatWrap = styled("div")`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;
  transition: padding 160ms ease-out;
  @container (max-width: 768px) {
    padding: 0 8px;
  }
`;

const NavWrap = styled("div")`
  position: absolute;
  top: 0;
  bottom: 32px;
  right: 14px;
  width: 20px;
  pointer-events: none;
`;

const Controls = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RightControls = styled(Controls)`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
`;

// ChatWorkspace remounts per tab (keyed by personalChatId), so the open output
// and the dragged panel width are kept here to survive tab switches.
type WorkspacePanel =
  | { type: "outputs" }
  | {
      type: "output";
      output: ChatOutputView;
      parent: WorkspacePanel | null;
    }
  | {
      type: "archive-file";
      file: FilePreviewTarget;
      parent: WorkspacePanel;
    }
  | {
      type: "sources";
      messageId?: string;
    }
  | { type: "page"; link: WorkflowPageLink; parent: WorkspacePanel | null };
const openPanelByChatId = new Map<string, WorkspacePanel>();
const panelOpenByChatId = new Map<string, boolean>();
const elementSnippetsByChatId = new Map<string, ElementSnippet[]>();
const openedPageCallsByChatId = new Map<string, string>();

function deselectOutputElement(snippet: ElementSnippet) {
  window.dispatchEvent(
    new CustomEvent("3b-deselect-element", {
      detail: { index: snippet.index, sourceId: snippet.sourceId },
    })
  );
}

function panelSection(panel: WorkspacePanel | null): ChatpadSection {
  if (!panel) return "outputs";
  if (panel.type === "page") return panelSection(panel.parent);
  return panel.type === "sources" ? "sources" : "outputs";
}

function panelParent(
  panel: WorkspacePanel | null
): WorkspacePanel | null | undefined {
  switch (panel?.type) {
    case "output":
    case "archive-file":
    case "page":
      return panel.parent;
    default:
      return undefined;
  }
}

function panelBackLabel(panel: WorkspacePanel | null | undefined) {
  switch (panel?.type) {
    case "outputs":
      return "Back to work";
    case "sources":
      return "Back to sources";
    case "output":
      return `Back to ${panel.output.fileName}`;
    case "archive-file":
      return `Back to ${panel.file.fileName}`;
    case "page":
      return `Back to ${panel.link.route}`;
    default:
      return "Back to work";
  }
}

function panelDepth(panel: WorkspacePanel | null | undefined): number {
  switch (panel?.type) {
    case "output":
    case "archive-file":
    case "page":
      return panelDepth(panel.parent) + 1;
    default:
      return 0;
  }
}

function panelViewKey(panel: WorkspacePanel | null): string {
  switch (panel?.type) {
    case "output":
      return `output:${panel.output.key}`;
    case "archive-file":
      return `${panelViewKey(panel.parent)}/${panel.file.fileName}`;
    case "page":
      return `page:${panel.link.href}`;
    default:
      return panel?.type ?? "outputs";
  }
}

export function ChatWorkspace({
  personalChatId,
  chatOwnerId,
  chat,
  pending,
  readOnly = false,
  onRenameChat,
  onForkChat,
  onDownloadChat,
  onCloseChat,
  onTogglePin,
  pinned,
}: Props) {
  const z = useZero();
  const orchestratorEnabled = useFeatureFlag(CHAT_ORCHESTRATOR_FLAG) === true;
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const [currentUser] = useQuery(queries.currentUser());
  const userName = greetingName(currentUser?.name ?? "");
  const [personalChat] = useQuery(
    queries.loadPersonalChat({ personalChatId, userId: chatOwnerId })
  );
  const mentionConnectors = useMemo(() => {
    const seen = new Set<string>();
    return (personalChat?.appUsages ?? []).flatMap((row) => {
      const app = row.connectedApp;
      if (!app || seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [personalChat?.appUsages]);
  const [tenantConnectorsRaw] = useQuery(queries.connectorsIndex());
  const mentionTenantConnectors = useMemo(() => {
    const seen = new Set<string>();
    return tenantConnectorsRaw.flatMap((app) => {
      if (seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [tenantConnectorsRaw]);
  const [pendingZip, setPendingZip] = useState<File | null>(null);
  const [elementSnippets, setElementSnippets] = useState<ElementSnippet[]>(
    () => elementSnippetsByChatId.get(personalChatId) ?? []
  );
  useEffect(() => {
    if (elementSnippets.length)
      elementSnippetsByChatId.set(personalChatId, elementSnippets);
    else elementSnippetsByChatId.delete(personalChatId);
  }, [personalChatId, elementSnippets]);
  const selectOutputElement = useCallback(
    (
      output: ChatOutputView,
      element: Omit<ElementSnippet, "stepName" | "color">
    ) => {
      const sourceId = output.key;
      setElementSnippets((previous) => [
        ...previous.filter(
          (snippet) =>
            snippet.sourceId !== sourceId || snippet.index !== element.index
        ),
        { ...element, sourceId, stepName: output.fileName, color: "sky" },
      ]);
    },
    []
  );
  const deselectElement = useCallback((sourceId: string, index: number) => {
    setElementSnippets((previous) =>
      previous.filter(
        (snippet) => snippet.sourceId !== sourceId || snippet.index !== index
      )
    );
  }, []);
  const removeElementSnippet = useCallback(
    (id: string) => {
      const snippet = elementSnippets.find((element) => element.id === id);
      if (snippet) deselectOutputElement(snippet);
      setElementSnippets((previous) =>
        previous.filter((element) => element.id !== id)
      );
    },
    [elementSnippets]
  );
  const clearElementSnippets = useCallback(() => {
    elementSnippets.forEach(deselectOutputElement);
    setElementSnippets([]);
  }, [elementSnippets]);
  const [sourceGroups, setSourceGroups] = useState<ResearchSourceGroup[]>([]);
  const [panel, setPanel] = useState<WorkspacePanel | null>(
    () => openPanelByChatId.get(personalChatId) ?? null
  );
  const [panelOpen, setPanelOpen] = useState(
    () => panelOpenByChatId.get(personalChatId) ?? false
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<ChatHandle>(null);
  const showOutput = useCallback((next: ChatOutputView) => {
    setPanel((previous) => {
      while (previous?.type === "archive-file") previous = previous.parent;
      return {
        type: "output",
        output: next,
        parent: previous?.type === "output" ? previous.parent : previous,
      };
    });
    setPanelOpen(true);
  }, []);
  const showArchiveFile = useCallback(
    (file: FilePreviewTarget) => {
      if (panel?.type !== "output" && panel?.type !== "archive-file") return;
      // Extraction may finish after the user navigates away from the archive.
      setPanel((previous) =>
        previous === panel
          ? { type: "archive-file", file, parent: panel }
          : previous
      );
    },
    [panel]
  );
  const showSources = useCallback((group: ResearchSourceGroup) => {
    setPanel({
      type: "sources",
      messageId: group.messageId,
    });
    setPanelOpen(true);
  }, []);
  const showAllSources = useCallback(() => {
    setPanel({ type: "sources" });
    setPanelOpen(true);
  }, []);
  const openPage = useCallback((link: WorkflowPageLink) => {
    setPanel((previous) => ({
      type: "page",
      link,
      parent: previous?.type === "page" ? previous.parent : previous,
    }));
    setPanelOpen(true);
  }, []);
  const showPage = useCallback(
    (view: WorkflowPageView) => {
      const link = workflowPageLink(
        getPageUrl(view.spaceSlug, view.route, edgeUrlOptions, {
          branchId: view.branchId,
          externalId: view.externalId,
        }),
        edgeUrlOptions
      );
      if (link) openPage({ ...link, workflowId: view.workflowId });
    },
    [openPage, edgeUrlOptions]
  );
  const viewedPageWorkflowId =
    panel?.type === "page" ? panel.link.workflowId : undefined;
  useEffect(() => {
    if (viewedPageWorkflowId)
      z.mutate(
        sharedMutators.markWorkflowViewed({ workflowId: viewedPageWorkflowId })
      );
  }, [viewedPageWorkflowId, z]);
  const [previewTurnStartedAt, setPreviewTurnStartedAt] = useState(
    chat.respondingAt
  );
  if (chat.respondingAt != null && chat.respondingAt !== previewTurnStartedAt)
    setPreviewTurnStartedAt(chat.respondingAt);
  const pageRequest =
    previewTurnStartedAt != null
      ? chat.entries.findLast(
          (entry) =>
            entry.createdAt >= previewTurnStartedAt &&
            entry.toolCall?.toolName === "showWorkflowPage" &&
            !entry.toolCall.isError
        )
      : undefined;
  const { output: requestedPage } = useToolOutput(
    pageRequest?.id ?? "",
    pageRequest?.toolCall?.completedAt != null
  );
  const pageRequestId = pageRequest?.id;
  useEffect(() => {
    if (
      !pageRequestId ||
      openedPageCallsByChatId.get(personalChatId) === pageRequestId
    )
      return;
    const view = workflowPageViewSchema.safeParse(requestedPage);
    if (!view.success) return;
    openedPageCallsByChatId.set(personalChatId, pageRequestId);
    // oxlint-disable-next-line react/react-compiler -- Navigation follows asynchronously loaded tool results.
    showPage(view.data);
  }, [pageRequestId, personalChatId, requestedPage, showPage]);
  const investigationDraft = useChatInvestigation(
    personalChatId,
    readOnly,
    chat.entries.length > 0
  );
  const investigation = investigationDraft.context;
  const investigationDetails = investigation
    ? getInvestigationContent(investigation.surface)
    : undefined;
  const renderEmptyState = useCallback(
    ({
      fillInput,
      visible,
    }: ComponentProps<NonNullable<ChatProps["EmptyState"]>>) => (
      <NewChatEmptyState
        onPick={fillInput}
        iconColor={`var(--${pickChatIconColor(personalChatId)}-600)`}
        userName={userName}
        seed={personalChatId}
        visible={visible}
        content={investigationDetails}
      />
    ),
    [personalChatId, userName, investigationDetails]
  );
  const closeOutputs = useCallback(() => setPanelOpen(false), []);
  const showOutputs = useCallback(() => {
    setPanel({ type: "outputs" });
    setPanelOpen(true);
  }, []);
  const showSection = useCallback(
    (section: ChatpadSection) => {
      if (section === "outputs") showOutputs();
      else showAllSources();
    },
    [showOutputs, showAllSources]
  );
  const toggleChatpad = useCallback(() => setPanelOpen((open) => !open), []);
  const goBack = useCallback(() => {
    setPanel((previous) => panelParent(previous) ?? null);
    setPanelOpen(true);
  }, []);
  const sourcesContext = useMemo(
    () => ({ showSources, onSourcesChange: setSourceGroups }),
    [showSources]
  );
  const outputs = useMemo(
    () => deriveChatOutputs(chat.entries as ChatEntryWithSubTables[]),
    [chat.entries]
  );
  useEffect(() => {
    panelOpenByChatId.set(personalChatId, panelOpen);
    if (panel) openPanelByChatId.set(personalChatId, panel);
    else openPanelByChatId.delete(personalChatId);
  }, [personalChatId, panel, panelOpen]);
  useChatpadShortcut(toggleChatpad);
  const pendingSubAgentCall = usePendingSubAgentCall(chat.id);
  const tools: readonly ChatTool[] = useMemo(
    () => [
      askQuestion,
      approvePlan,
      bash({ getStepColor: () => undefined }),
      connectChatToApp({ personalChatId }),
      connectToApp({ personalChatId }),
      editFile({ getStepColor: () => undefined }),
      execute,
      ...(orchestratorEnabled
        ? subAgentTools(chat.id)
        : [setWorkflowSuggestion]),
      showExecutionOutput({
        respondingAt: chat.respondingAt,
        onShowOutput: showOutput,
      }),
      showStorageFile({
        respondingAt: chat.respondingAt,
        onShowOutput: showOutput,
      }),
      showWorkflowPage(showPage),
      readFile({ getStepColor: () => undefined }),
      readStorageFile,
      skillTool,
      readSkillFile,
      runSkillScript,
      webFetch,
      webSearch,
      writeFile({ getStepColor: () => undefined }),
    ],
    [
      chat.respondingAt,
      chat.id,
      personalChatId,
      showOutput,
      showPage,
      orchestratorEnabled,
    ]
  );

  useEffect(() => {
    if (pending) return;
    z.mutate(sharedMutators.touchPersonalChat({ id: personalChatId }));
  }, [personalChatId, pending, z]);

  const handleSend = useCallback<ChatProps["onSend"]>(
    (payload, onError) => {
      const result = z.mutate(
        sharedMutators.sendPersonalChatMessage({
          chatId: chat.id,
          entryId: nanoid(12),
          text: payload.text,
          isDictated: payload.isDictated,
          effort: payload.effort,
          chatModel: payload.chatModel,
          attachments: payload.attachments,
          interruptEntryId: payload.interruptEntryId,
          ...clientSendContext(),
        })
      );
      void result.server.then((r) => {
        if (r.type === "error") onError();
      }, onError);
    },
    [chat.id, z]
  );

  const handleInterrupt = () => {
    z.mutate(
      sharedMutators.interruptPersonalChat({
        entryId: nanoid(12),
        chatId: chat.id,
      })
    );
  };

  const handleProvideToolResult = useCallback<ChatProps["onProvideToolResult"]>(
    async (entryId, output, isError, options) => {
      const showSaveError = () => {
        emitErrorToast({
          icon: "circle-off",
          message: "The tool result couldn’t be saved.",
          buttonLabel: "Dismiss",
        });
        return false;
      };
      try {
        const outputId = await uploadToolCallOutput({
          chatId: chat.id,
          entryId,
          output,
        });
        const result = await z.mutate(
          sharedMutators.providePersonalChatToolResult({
            chatId: chat.id,
            entryId,
            outputId,
            isError,
            pauseAfter: options?.pauseAfter,
            chatModel: options?.chatModel,
          })
        ).server;
        if (result.type === "error") return showSaveError();
        return true;
      } catch {
        return showSaveError();
      }
    },
    [chat.id, z]
  );

  const chatPane = (
    <Container ref={containerRef}>
      <RightControls>
        <Controls style={readOnly ? readOnlyStyle : undefined}>
          {!orchestratorEnabled && (
            <DeriveWorkflowButton
              personalChatId={personalChatId}
              chatOwnerId={chatOwnerId}
            />
          )}
        </Controls>
        <ChatpadToggleButton
          floating
          isOpen={panelOpen}
          onToggle={toggleChatpad}
        />
      </RightControls>
      <ScrollArea ref={scrollRef}>
        <ChatWrap>
          <Chat
            key={chat.id}
            ref={chatRef}
            modelScope={{ type: "personal" }}
            onZipFile={chat.entries.length > 0 ? undefined : setPendingZip}
            acceptAnyFile
            entries={chat.entries}
            writeChatId={chat.id}
            draftKey={personalChatId}
            onSend={handleSend}
            onInterrupt={handleInterrupt}
            onProvideToolResult={handleProvideToolResult}
            isCurrentUserChat={!readOnly}
            canWrite={!readOnly}
            tools={tools}
            elementSnippets={elementSnippets}
            removeElementSnippet={removeElementSnippet}
            clearElementSnippets={clearElementSnippets}
            mentionConnectors={mentionConnectors}
            mentionTenantConnectors={mentionTenantConnectors}
            mentionConnectorCatalog={mentionConnectorCatalog}
            mentionConnectorContextLabel="On this chat"
            composerContext={{
              ...investigationDraft,
              pill:
                investigation && investigationDetails ? (
                  <ChatInvestigationSource
                    context={investigation}
                    onRemove={investigationDraft.clear}
                  />
                ) : undefined,
            }}
            suggestions={chatSuggestions}
            placeholder="Ask anything across your tools and data"
            EmptyState={renderEmptyState}
            soundsEnabled={!readOnly}
            respondingAt={chat.respondingAt}
            lastLoopHeartbeatAt={chat.lastLoopHeartbeatAt}
            awaitingAction={chat.awaitingAction ?? false}
            pendingPromptOverride={
              pendingSubAgentCall &&
              !readOnly &&
              !chat.awaitingAction && (
                <SubAgentPrompt {...pendingSubAgentCall} />
              )
            }
            planModeEnabled={false}
            resolveFileHref={
              readOnly
                ? undefined
                : (href) => resolveStorageFileHref(href, personalVolumePath)
            }
            outerScroll
            outerScrollRef={scrollRef}
          />
          {orchestratorEnabled && !readOnly && (
            <SubAgentPage chatId={chat.id} onShowPage={showPage} />
          )}
          {pendingZip && (
            <ImportWorkflowModal
              file={pendingZip}
              onClose={() => setPendingZip(null)}
            />
          )}
        </ChatWrap>
      </ScrollArea>
      <NavWrap>
        <ChatMessageNav
          entries={chat.entries as ChatEntryWithSubTables[]}
          scrollContainer={scrollRef}
          wrapperRef={containerRef}
        />
      </NavWrap>
    </Container>
  );

  const backLabel = panelBackLabel(panelParent(panel));

  return (
    <ResearchSourcesPanelContext.Provider value={sourcesContext}>
      <ChatWorkspaceLayout
        open={panelOpen}
        onClose={closeOutputs}
        panel={
          <Chatpad
            open={panelOpen}
            onToggle={toggleChatpad}
            activeSection={panelSection(panel)}
            onSelectSection={showSection}
            resources={
              <>
                <PersonalChatConnectorsBar
                  personalChatId={personalChatId}
                  chatOwnerId={chatOwnerId}
                  readOnly={readOnly}
                />
                <PersonalChatVolumesButton disabled={readOnly} />
              </>
            }
            actions={
              <ChatActionsMenu
                disabled={readOnly}
                onRename={onRenameChat}
                onFork={onForkChat}
                onDownload={onDownloadChat}
                onClose={onCloseChat}
                onTogglePin={onTogglePin}
                pinned={pinned}
              />
            }
          >
            <PanelTransition
              viewKey={panelViewKey(panel)}
              depth={panelDepth(panel)}
            >
              {panel?.type === "sources" ? (
                <SourcesPanel
                  embedded
                  groups={sourceGroups}
                  scrollTarget={panel}
                  onClose={closeOutputs}
                />
              ) : panel?.type === "output" ? (
                <FilePreviewPanel
                  key={panelViewKey(panel)}
                  embedded
                  url={panel.output.url}
                  fileName={panel.output.fileName}
                  sizeBytes={panel.output.sizeBytes}
                  onBack={goBack}
                  backLabel={backLabel}
                  interactive
                  htmlInspection={
                    readOnly
                      ? undefined
                      : {
                          sourceId: panel.output.key,
                          elementSnippets: elementSnippets.filter(
                            (snippet) => snippet.sourceId === panel.output.key
                          ),
                          onElementSelect: (element) =>
                            selectOutputElement(panel.output, element),
                          onElementDeselect: (index) =>
                            deselectElement(panel.output.key, index),
                        }
                  }
                  onOpenFile={showArchiveFile}
                />
              ) : panel?.type === "archive-file" ? (
                <FilePreviewPanel
                  key={panelViewKey(panel)}
                  embedded
                  {...panel.file}
                  onBack={goBack}
                  backLabel={backLabel}
                  interactive
                  onOpenFile={showArchiveFile}
                />
              ) : panel?.type === "page" ? (
                <StepDetails
                  embedded
                  heading={null}
                  flushBody
                  body={<WorkflowPagePreview link={panel.link} />}
                />
              ) : (
                <OutputsList outputs={outputs} onShowOutput={showOutput} />
              )}
            </PanelTransition>
          </Chatpad>
        }
      >
        {chatPane}
      </ChatWorkspaceLayout>
    </ResearchSourcesPanelContext.Provider>
  );
}
